import { sql } from "drizzle-orm";
import type { WorkflowV2RunStatus } from "./workflow-kernel-v2-state.js";
import { acquireWorkflowV2CapacityLocks } from "./workflow-kernel-v2-capacity-locks.js";
import { executeWorkflowV2Query, type WorkflowV2DbLike } from "./workflow-kernel-v2-db.js";
import { workflowV2Jsonb } from "./workflow-kernel-v2-json.js";
import type { WorkflowV2CapacityPolicy } from "./workflow-kernel-v2-capacity.js";

export type { WorkflowV2DbLike };

export type WorkflowV2RunRow = {
  id: string;
  workflow_name: string;
  workflow_version: string | null;
  status: WorkflowV2RunStatus;
  args_json: unknown;
  user_id: string | null;
  result_json: unknown;
  error_json: unknown;
  error_code: string | null;
  current_step_key: string | null;
  priority: number;
  runnable_at: string | Date;
  lease_owner: string | null;
  lease_epoch: number;
  lease_expires_at: string | Date | null;
  heartbeat_at: string | Date | null;
  attempt_count: number;
  max_attempts: number;
  retry_count: number;
  max_retries: number;
  max_active_duration_ms: number;
  active_started_at: string | Date | null;
  active_deadline_at: string | Date | null;
  lifecycle_deadline_at: string | Date | null;
  cancel_requested_at: string | Date | null;
  idempotency_key: string | null;
  created_at: string | Date;
  updated_at: string | Date;
  completed_at: string | Date | null;
};

export type CreateWorkflowV2RunInput = {
  id: string;
  workflowName: string;
  workflowVersion?: string | null;
  args?: unknown;
  userId?: string | null;
  priority?: number;
  runnableAt?: string | Date;
  maxAttempts?: number;
  maxRetries?: number;
  maxActiveDurationMs?: number;
  activeDeadlineAt?: string | Date | null;
  lifecycleDeadlineAt?: string | Date | null;
  idempotencyKey?: string | null;
};

export const WORKFLOW_V2_SYSTEM_SCHEMA_STATEMENTS = [
  `CREATE TABLE IF NOT EXISTS _gencow_workflow_runs_v2 (
        id TEXT PRIMARY KEY,
        workflow_name TEXT NOT NULL,
        workflow_version TEXT,
        status TEXT NOT NULL DEFAULT 'queued',
        args_json JSONB NOT NULL DEFAULT '{}'::jsonb,
        user_id TEXT,
        result_json JSONB,
        error_json JSONB,
        error_code TEXT,
        current_step_key TEXT,
        priority INTEGER NOT NULL DEFAULT 0,
        runnable_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
        lease_owner TEXT,
        lease_epoch INTEGER NOT NULL DEFAULT 0,
        lease_expires_at TIMESTAMPTZ,
        heartbeat_at TIMESTAMPTZ,
        attempt_count INTEGER NOT NULL DEFAULT 0,
        max_attempts INTEGER NOT NULL DEFAULT 3,
        retry_count INTEGER NOT NULL DEFAULT 0,
        max_retries INTEGER NOT NULL DEFAULT 3,
        max_active_duration_ms BIGINT NOT NULL DEFAULT 1800000,
        active_started_at TIMESTAMPTZ,
        active_deadline_at TIMESTAMPTZ,
        lifecycle_deadline_at TIMESTAMPTZ,
        cancel_requested_at TIMESTAMPTZ,
        idempotency_key TEXT,
        created_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
        updated_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
        completed_at TIMESTAMPTZ,
        UNIQUE(idempotency_key)
    )`,
  `ALTER TABLE _gencow_workflow_runs_v2 ADD COLUMN IF NOT EXISTS user_id TEXT`,
  `ALTER TABLE _gencow_workflow_runs_v2 ADD COLUMN IF NOT EXISTS retry_count INTEGER NOT NULL DEFAULT 0`,
  `ALTER TABLE _gencow_workflow_runs_v2 ADD COLUMN IF NOT EXISTS max_retries INTEGER NOT NULL DEFAULT 3`,
  `ALTER TABLE _gencow_workflow_runs_v2 ADD COLUMN IF NOT EXISTS max_active_duration_ms BIGINT NOT NULL DEFAULT 1800000`,
  `CREATE INDEX IF NOT EXISTS idx__gencow_workflow_runs_v2_claim
        ON _gencow_workflow_runs_v2 (status, runnable_at, priority DESC, created_at)
        WHERE completed_at IS NULL`,
  `CREATE INDEX IF NOT EXISTS idx__gencow_workflow_runs_v2_lease
        ON _gencow_workflow_runs_v2 (lease_expires_at)
        WHERE status = 'running' AND completed_at IS NULL`,
  `CREATE TABLE IF NOT EXISTS _gencow_workflow_steps_v2 (
        id BIGSERIAL PRIMARY KEY,
        run_id TEXT NOT NULL REFERENCES _gencow_workflow_runs_v2(id) ON DELETE CASCADE,
        step_key TEXT NOT NULL,
        step_index INTEGER NOT NULL DEFAULT 0,
        status TEXT NOT NULL DEFAULT 'pending',
        input_hash TEXT,
        output_json JSONB,
        error_json JSONB,
        error_code TEXT,
        attempt_count INTEGER NOT NULL DEFAULT 0,
        max_attempts INTEGER NOT NULL DEFAULT 3,
        runnable_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
        lease_owner TEXT,
        lease_epoch INTEGER NOT NULL DEFAULT 0,
        lease_expires_at TIMESTAMPTZ,
        started_at TIMESTAMPTZ,
        heartbeat_at TIMESTAMPTZ,
        completed_at TIMESTAMPTZ,
        updated_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
        UNIQUE(run_id, step_key)
    )`,
  `CREATE INDEX IF NOT EXISTS idx__gencow_workflow_steps_v2_lookup
        ON _gencow_workflow_steps_v2 (run_id, step_key)`,
  `CREATE TABLE IF NOT EXISTS _gencow_workflow_timers_v2 (
        id TEXT PRIMARY KEY,
        run_id TEXT NOT NULL REFERENCES _gencow_workflow_runs_v2(id) ON DELETE CASCADE,
        kind TEXT NOT NULL,
        fire_at TIMESTAMPTZ NOT NULL,
        status TEXT NOT NULL DEFAULT 'pending',
        dedupe_key TEXT,
        created_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
        updated_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
        UNIQUE(run_id, dedupe_key)
    )`,
  `CREATE INDEX IF NOT EXISTS idx__gencow_workflow_timers_v2_due
        ON _gencow_workflow_timers_v2 (status, fire_at)`,
  `CREATE TABLE IF NOT EXISTS _gencow_workflow_signals_v2 (
        id TEXT PRIMARY KEY,
        run_id TEXT NOT NULL REFERENCES _gencow_workflow_runs_v2(id) ON DELETE CASCADE,
        event_name TEXT NOT NULL,
        payload_json JSONB,
        idempotency_key TEXT,
        received_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
        consumed_at TIMESTAMPTZ,
        UNIQUE(run_id, event_name, idempotency_key)
    )`,
  `CREATE INDEX IF NOT EXISTS idx__gencow_workflow_signals_v2_wait
        ON _gencow_workflow_signals_v2 (run_id, event_name, received_at)
        WHERE consumed_at IS NULL`,
  `CREATE TABLE IF NOT EXISTS _gencow_workflow_outbox_v2 (
        id TEXT PRIMARY KEY,
        run_id TEXT NOT NULL REFERENCES _gencow_workflow_runs_v2(id) ON DELETE CASCADE,
        kind TEXT NOT NULL,
        available_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
        status TEXT NOT NULL DEFAULT 'pending',
        attempt_count INTEGER NOT NULL DEFAULT 0,
        created_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
        updated_at TIMESTAMPTZ NOT NULL DEFAULT NOW()
    )`,
  `CREATE INDEX IF NOT EXISTS idx__gencow_workflow_outbox_v2_due
        ON _gencow_workflow_outbox_v2 (status, available_at)`,
  `CREATE TABLE IF NOT EXISTS _gencow_workflow_capacity_locks_v2 (
        scope TEXT PRIMARY KEY,
        updated_at TIMESTAMPTZ NOT NULL DEFAULT NOW()
    )`,
  `CREATE TABLE IF NOT EXISTS _gencow_workflow_events_v2 (
        id BIGSERIAL PRIMARY KEY,
        run_id TEXT NOT NULL REFERENCES _gencow_workflow_runs_v2(id) ON DELETE CASCADE,
        event_type TEXT NOT NULL,
        event_json JSONB,
        created_at TIMESTAMPTZ NOT NULL DEFAULT NOW()
    )`,
  `CREATE INDEX IF NOT EXISTS idx__gencow_workflow_events_v2_timeline
        ON _gencow_workflow_events_v2 (run_id, created_at, id)`,
] as const;

function rowsFromResult<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  if (result && typeof result === "object" && Array.isArray((result as { rows?: unknown[] }).rows)) {
    return (result as { rows: T[] }).rows;
  }
  return [];
}

function nullableTimestamp(value: string | Date | null | undefined): ReturnType<typeof sql> {
  return value == null ? sql`NULL` : sql`${value}::timestamptz`;
}

export async function createWorkflowV2Run(
  db: WorkflowV2DbLike,
  input: CreateWorkflowV2RunInput,
): Promise<WorkflowV2RunRow> {
  const result = await executeWorkflowV2Query(
    db,
    sql`
        INSERT INTO _gencow_workflow_runs_v2 (
            id,
            workflow_name,
            workflow_version,
            args_json,
            user_id,
            priority,
            runnable_at,
            max_attempts,
            retry_count,
            max_retries,
            max_active_duration_ms,
            active_deadline_at,
            lifecycle_deadline_at,
            idempotency_key
        )
        VALUES (
            ${input.id},
            ${input.workflowName},
            ${input.workflowVersion ?? null},
            ${workflowV2Jsonb(input.args ?? {})},
            ${input.userId ?? null},
            ${input.priority ?? 0},
            ${input.runnableAt ?? sql`NOW()`},
            ${input.maxAttempts ?? 3},
            0,
            ${input.maxRetries ?? 3},
            ${input.maxActiveDurationMs ?? 1_800_000},
            ${nullableTimestamp(input.activeDeadlineAt)},
            ${nullableTimestamp(input.lifecycleDeadlineAt)},
            ${input.idempotencyKey ?? null}
        )
        RETURNING *
    `,
  );
  const row = rowsFromResult<WorkflowV2RunRow>(result)[0];
  if (!row) throw new Error(`failed to create workflow v2 run "${input.id}"`);
  return row;
}

export async function claimNextWorkflowV2Run(options: {
  db: WorkflowV2DbLike;
  workerId: string;
  leaseMs: number;
}): Promise<WorkflowV2RunRow | null> {
  const result = await executeWorkflowV2Query(
    options.db,
    sql`
        WITH candidate AS (
            SELECT id
            FROM _gencow_workflow_runs_v2
            WHERE status IN ('queued', 'running')
              AND runnable_at <= NOW()
              AND completed_at IS NULL
              AND cancel_requested_at IS NULL
              AND (lease_expires_at IS NULL OR lease_expires_at < NOW())
            ORDER BY priority DESC, runnable_at ASC, created_at ASC
            FOR UPDATE SKIP LOCKED
            LIMIT 1
        )
        UPDATE _gencow_workflow_runs_v2
        SET
            status = 'running',
            lease_owner = ${options.workerId},
            lease_epoch = lease_epoch + 1,
            lease_expires_at = NOW() + (${options.leaseMs} * INTERVAL '1 millisecond'),
            heartbeat_at = NOW(),
            active_started_at = NOW(),
            active_deadline_at = NOW() + (max_active_duration_ms * INTERVAL '1 millisecond'),
            attempt_count = attempt_count + 1,
            error_json = NULL,
            error_code = NULL,
            updated_at = NOW()
        WHERE id = (SELECT id FROM candidate)
        RETURNING *
    `,
  );
  return rowsFromResult<WorkflowV2RunRow>(result)[0] ?? null;
}

export async function claimWorkflowV2RunById(options: {
  db: WorkflowV2DbLike;
  runId: string;
  workerId: string;
  leaseMs: number;
  capacityPolicy?: WorkflowV2CapacityPolicy;
}): Promise<WorkflowV2RunRow | null> {
  const globalConcurrency = positiveCapacityLimit(options.capacityPolicy?.globalConcurrency);
  const perUserConcurrency = positiveCapacityLimit(options.capacityPolicy?.perUserConcurrency);
  const perWorkflowNameConcurrency = positiveCapacityLimit(
    options.capacityPolicy?.perWorkflowNameConcurrency,
  );
  if (
    (globalConcurrency || perUserConcurrency || perWorkflowNameConcurrency) &&
    typeof options.db.transaction === "function"
  ) {
    return options.db.transaction(async (tx) => {
      await acquireWorkflowV2CapacityLocks({
        db: tx,
        runId: options.runId,
        globalConcurrency,
        perUserConcurrency,
        perWorkflowNameConcurrency,
      });
      return claimWorkflowV2RunByIdOnce({
        ...options,
        db: tx,
        globalConcurrency,
        perUserConcurrency,
        perWorkflowNameConcurrency,
      });
    });
  }
  return claimWorkflowV2RunByIdOnce({
    ...options,
    globalConcurrency,
    perUserConcurrency,
    perWorkflowNameConcurrency,
  });
}

async function claimWorkflowV2RunByIdOnce(options: {
  db: WorkflowV2DbLike;
  runId: string;
  workerId: string;
  leaseMs: number;
  globalConcurrency: number | null;
  perUserConcurrency: number | null;
  perWorkflowNameConcurrency: number | null;
}): Promise<WorkflowV2RunRow | null> {
  const result = await executeWorkflowV2Query(
    options.db,
    sql`
        UPDATE _gencow_workflow_runs_v2
        SET
            status = 'running',
            lease_owner = ${options.workerId},
            lease_epoch = lease_epoch + 1,
            lease_expires_at = NOW() + (${options.leaseMs} * INTERVAL '1 millisecond'),
            heartbeat_at = NOW(),
            active_started_at = NOW(),
            active_deadline_at = NOW() + (max_active_duration_ms * INTERVAL '1 millisecond'),
            attempt_count = attempt_count + 1,
            error_json = NULL,
            error_code = NULL,
            updated_at = NOW()
        WHERE id = ${options.runId}
          AND status IN ('queued', 'running')
          AND runnable_at <= NOW()
          AND completed_at IS NULL
          AND cancel_requested_at IS NULL
          AND (lease_expires_at IS NULL OR lease_expires_at < NOW())
          AND (
            ${options.globalConcurrency}::integer IS NULL
            OR (
              SELECT COUNT(*)::integer
              FROM _gencow_workflow_runs_v2 running
              WHERE running.status = 'running'
                AND running.completed_at IS NULL
                AND running.cancel_requested_at IS NULL
                AND running.lease_expires_at > NOW()
            ) < ${options.globalConcurrency}::integer
          )
          AND (
            ${options.perWorkflowNameConcurrency}::integer IS NULL
            OR (
              SELECT COUNT(*)::integer
              FROM _gencow_workflow_runs_v2 running
              WHERE running.status = 'running'
                AND running.completed_at IS NULL
                AND running.cancel_requested_at IS NULL
                AND running.lease_expires_at > NOW()
                AND running.workflow_name = _gencow_workflow_runs_v2.workflow_name
            ) < ${options.perWorkflowNameConcurrency}::integer
          )
          AND (
            ${options.perUserConcurrency}::integer IS NULL
            OR _gencow_workflow_runs_v2.user_id IS NULL
            OR (
              SELECT COUNT(*)::integer
              FROM _gencow_workflow_runs_v2 running
              WHERE running.status = 'running'
                AND running.completed_at IS NULL
                AND running.cancel_requested_at IS NULL
                AND running.lease_expires_at > NOW()
                AND running.user_id = _gencow_workflow_runs_v2.user_id
            ) < ${options.perUserConcurrency}::integer
          )
        RETURNING *
    `,
  );
  return rowsFromResult<WorkflowV2RunRow>(result)[0] ?? null;
}

function positiveCapacityLimit(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? Math.floor(value) : null;
}

export async function getWorkflowV2CapacitySnapshot(db: WorkflowV2DbLike): Promise<{
  globalRunning: number;
  runningByUserId: Record<string, number>;
  runningByWorkflowName: Record<string, number>;
}> {
  const result = await executeWorkflowV2Query(
    db,
    sql`
        SELECT workflow_name, user_id, COUNT(*)::int AS count
        FROM _gencow_workflow_runs_v2
        WHERE status = 'running'
          AND completed_at IS NULL
          AND cancel_requested_at IS NULL
          AND lease_expires_at > NOW()
        GROUP BY workflow_name, user_id
    `,
  );
  const rows = rowsFromResult<{ workflow_name: string; user_id: string | null; count: number | string }>(
    result,
  );
  const runningByWorkflowName: Record<string, number> = {};
  const runningByUserId: Record<string, number> = {};
  let globalRunning = 0;
  for (const row of rows) {
    const count = Number(row.count) || 0;
    runningByWorkflowName[row.workflow_name] = (runningByWorkflowName[row.workflow_name] ?? 0) + count;
    if (row.user_id) {
      runningByUserId[row.user_id] = (runningByUserId[row.user_id] ?? 0) + count;
    }
    globalRunning += count;
  }
  return { globalRunning, runningByUserId, runningByWorkflowName };
}

export async function deferWorkflowV2RunForCapacity(options: {
  db: WorkflowV2DbLike;
  runId: string;
  retryAfterMs: number;
  reason: string;
  signal: string;
}): Promise<WorkflowV2RunRow | null> {
  const result = await executeWorkflowV2Query(
    options.db,
    sql`
      WITH deferred AS (
        UPDATE _gencow_workflow_runs_v2
        SET
            status = 'queued',
            runnable_at = NOW() + (${options.retryAfterMs} * INTERVAL '1 millisecond'),
            error_json = ${workflowV2Jsonb({ message: "Workflow capacity deferred", reason: options.reason })},
            error_code = ${options.signal},
            lease_owner = NULL,
            lease_expires_at = NULL,
            heartbeat_at = NULL,
            active_started_at = NULL,
            active_deadline_at = NULL,
            updated_at = NOW()
        WHERE id = ${options.runId}
          AND status = 'queued'
          AND completed_at IS NULL
          AND cancel_requested_at IS NULL
        RETURNING *
      ),
      wake_outbox AS (
        INSERT INTO _gencow_workflow_outbox_v2 (
          id,
          run_id,
          kind,
          available_at,
          status
        )
        SELECT
          'capacity:' || id,
          id,
          'wake_capacity',
          runnable_at,
          'pending'
        FROM deferred
        ON CONFLICT (id) DO UPDATE SET
          available_at = EXCLUDED.available_at,
          status = 'pending',
          updated_at = NOW()
        RETURNING id
      )
      SELECT *
      FROM deferred
    `,
  );
  return rowsFromResult<WorkflowV2RunRow>(result)[0] ?? null;
}

export async function heartbeatWorkflowV2Run(options: {
  db: WorkflowV2DbLike;
  runId: string;
  leaseOwner: string;
  leaseEpoch: number;
  leaseMs: number;
}): Promise<boolean> {
  const result = await executeWorkflowV2Query(
    options.db,
    sql`
        UPDATE _gencow_workflow_runs_v2
        SET
            lease_expires_at = NOW() + (${options.leaseMs} * INTERVAL '1 millisecond'),
            heartbeat_at = NOW(),
            updated_at = NOW()
        WHERE id = ${options.runId}
          AND status = 'running'
          AND lease_owner = ${options.leaseOwner}
          AND lease_epoch = ${options.leaseEpoch}
          AND cancel_requested_at IS NULL
          AND completed_at IS NULL
        RETURNING id
    `,
  );
  return rowsFromResult<{ id: string }>(result).length === 1;
}

export async function getWorkflowV2Run(
  db: WorkflowV2DbLike,
  runId: string,
): Promise<WorkflowV2RunRow | null> {
  const result = await executeWorkflowV2Query(
    db,
    sql`
        SELECT *
        FROM _gencow_workflow_runs_v2
        WHERE id = ${runId}
        LIMIT 1
    `,
  );
  return rowsFromResult<WorkflowV2RunRow>(result)[0] ?? null;
}

export async function markWorkflowV2RunCurrentStep(options: {
  db: WorkflowV2DbLike;
  runId: string;
  leaseOwner: string;
  leaseEpoch: number;
  stepKey: string | null;
}): Promise<boolean> {
  const result = await executeWorkflowV2Query(
    options.db,
    sql`
        UPDATE _gencow_workflow_runs_v2
        SET
            current_step_key = ${options.stepKey},
            updated_at = NOW()
        WHERE id = ${options.runId}
          AND status = 'running'
          AND lease_owner = ${options.leaseOwner}
          AND lease_epoch = ${options.leaseEpoch}
          AND completed_at IS NULL
        RETURNING id
    `,
  );
  return rowsFromResult<{ id: string }>(result).length === 1;
}

export async function completeWorkflowV2Run(options: {
  db: WorkflowV2DbLike;
  runId: string;
  leaseOwner: string;
  leaseEpoch: number;
  result?: unknown;
}): Promise<boolean> {
  const result = await executeWorkflowV2Query(
    options.db,
    sql`
        UPDATE _gencow_workflow_runs_v2
        SET
            status = 'completed',
            result_json = ${workflowV2Jsonb(options.result ?? null)},
            error_json = NULL,
            error_code = NULL,
            lease_owner = NULL,
            lease_expires_at = NULL,
            heartbeat_at = NULL,
            completed_at = NOW(),
            updated_at = NOW()
        WHERE id = ${options.runId}
          AND status = 'running'
          AND lease_owner = ${options.leaseOwner}
          AND lease_epoch = ${options.leaseEpoch}
          AND completed_at IS NULL
        RETURNING id
    `,
  );
  return rowsFromResult<{ id: string }>(result).length === 1;
}

export async function failWorkflowV2Run(options: {
  db: WorkflowV2DbLike;
  runId: string;
  leaseOwner: string;
  leaseEpoch: number;
  error: unknown;
  errorCode: string;
}): Promise<boolean> {
  const result = await executeWorkflowV2Query(
    options.db,
    sql`
        UPDATE _gencow_workflow_runs_v2
        SET
            status = 'failed',
            error_json = ${workflowV2Jsonb(options.error)},
            error_code = ${options.errorCode},
            lease_owner = NULL,
            lease_expires_at = NULL,
            heartbeat_at = NULL,
            completed_at = NOW(),
            updated_at = NOW()
        WHERE id = ${options.runId}
          AND status = 'running'
          AND lease_owner = ${options.leaseOwner}
          AND lease_epoch = ${options.leaseEpoch}
          AND completed_at IS NULL
        RETURNING id
  `,
  );
  return rowsFromResult<{ id: string }>(result).length === 1;
}

export async function cancelClaimedWorkflowV2Run(options: {
  db: WorkflowV2DbLike;
  runId: string;
  leaseOwner: string;
  leaseEpoch: number;
  error?: unknown;
}): Promise<boolean> {
  const result = await executeWorkflowV2Query(
    options.db,
    sql`
        UPDATE _gencow_workflow_runs_v2
        SET
            status = 'cancelled',
            error_json = ${workflowV2Jsonb(options.error ?? { message: "Cancelled by user" })},
            error_code = 'cancelled',
            lease_owner = NULL,
            lease_expires_at = NULL,
            heartbeat_at = NULL,
            active_started_at = NULL,
            active_deadline_at = NULL,
            completed_at = NOW(),
            updated_at = NOW()
        WHERE id = ${options.runId}
          AND status = 'running'
          AND lease_owner = ${options.leaseOwner}
          AND lease_epoch = ${options.leaseEpoch}
          AND completed_at IS NULL
        RETURNING id
    `,
  );
  return rowsFromResult<{ id: string }>(result).length === 1;
}

export async function requeueWorkflowV2Run(options: {
  db: WorkflowV2DbLike;
  runId: string;
  leaseOwner: string;
  leaseEpoch: number;
  retryCount: number;
  error: unknown;
  errorCode: string;
  runnableAt: string | Date;
}): Promise<boolean> {
  const result = await executeWorkflowV2Query(
    options.db,
    sql`
      WITH requeued AS (
        UPDATE _gencow_workflow_runs_v2
        SET
            status = 'queued',
            retry_count = ${options.retryCount},
            error_json = ${workflowV2Jsonb(options.error)},
            error_code = ${options.errorCode},
            runnable_at = ${options.runnableAt}::timestamptz,
            lease_owner = NULL,
            lease_expires_at = NULL,
            heartbeat_at = NULL,
            active_started_at = NULL,
            active_deadline_at = NULL,
            completed_at = NULL,
            updated_at = NOW()
        WHERE id = ${options.runId}
          AND status = 'running'
          AND lease_owner = ${options.leaseOwner}
          AND lease_epoch = ${options.leaseEpoch}
          AND completed_at IS NULL
        RETURNING id, runnable_at
      ),
      wake_outbox AS (
        INSERT INTO _gencow_workflow_outbox_v2 (
          id,
          run_id,
          kind,
          available_at,
          status
        )
        SELECT
          'workflow-retry:' || id || ':' || ${options.retryCount},
          id,
          'wake_retry',
          runnable_at,
          'pending'
        FROM requeued
        ON CONFLICT (id) DO UPDATE SET
          available_at = EXCLUDED.available_at,
          status = 'pending',
          updated_at = NOW()
        RETURNING id
      )
      SELECT id
      FROM requeued
    `,
  );
  return rowsFromResult<{ id: string }>(result).length === 1;
}

export async function pauseWorkflowV2Run(options: {
  db: WorkflowV2DbLike;
  runId: string;
  leaseOwner: string;
  leaseEpoch: number;
  status: "sleeping" | "waiting" | "queued";
  currentStepKey: string | null;
  runnableAt?: string | Date | null;
}): Promise<boolean> {
  const result = await executeWorkflowV2Query(
    options.db,
    sql`
        UPDATE _gencow_workflow_runs_v2
        SET
            status = ${options.status},
            current_step_key = ${options.currentStepKey},
            runnable_at = COALESCE(${nullableTimestamp(options.runnableAt)}, runnable_at),
            lease_owner = NULL,
            lease_expires_at = NULL,
            heartbeat_at = NULL,
            active_started_at = NULL,
            active_deadline_at = NULL,
            updated_at = NOW()
        WHERE id = ${options.runId}
          AND status = 'running'
          AND lease_owner = ${options.leaseOwner}
          AND lease_epoch = ${options.leaseEpoch}
          AND completed_at IS NULL
        RETURNING id
    `,
  );
  return rowsFromResult<{ id: string }>(result).length === 1;
}

export async function requestCancelWorkflowV2Run(db: WorkflowV2DbLike, runId: string): Promise<boolean> {
  const result = await executeWorkflowV2Query(
    db,
    sql`
        UPDATE _gencow_workflow_runs_v2
        SET
            cancel_requested_at = COALESCE(cancel_requested_at, NOW()),
            updated_at = NOW()
        WHERE id = ${runId}
          AND status NOT IN ('completed', 'failed', 'cancelled')
          AND completed_at IS NULL
        RETURNING id
  `,
  );
  return rowsFromResult<{ id: string }>(result).length === 1;
}
